import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useActiveCohortSessionsStore } from '../stores/active-cohort-sessions.store';
import { useIncomingCallStore } from '../stores/incoming-call.store';

/**
 * RING-PUSH spec 4: the incoming-call notification goes away by itself once
 * the call it announces is settled, so a stale "Blake is calling" never sits
 * in the tray.
 *
 * Every channel tags it `incoming_call:{session}` (server
 * IncomingCallPush::tag; the native IncomingCallNotifier; public/sw.js), so
 * it is cancelled by tag:
 *
 *  - Android: the notification the app drew (IncomingCallNotifier, id
 *    NATIVE_NOTIFICATION_ID) and the one FCM drew for a build that could not
 *    (FCM's own display posts with id 0 under the message's tag);
 *  - web: the service worker's notifications with that tag.
 *
 * When: the sheet is on screen (IncomingCallSheet; the listener's RingSheet
 * does the same for its ring, cancelRingNotification: #1009 item 1, class
 * D); answered here (Join, Not now) or run out (all three mark the session
 * handled, watchIncomingCallDismissals); and when the poll brings the
 * session's `missed_call` (the caller gave up). With the app not
 * running, the native notification's own timeout (the window's end) does
 * it. There is no server-sent cancel: the FCM router has no data-only path
 * for an arbitrary user, and a tap on a notification the caller already
 * left lands on the "couldn't wait" card, never on a call.
 */

/** IncomingCallNotifier.NOTIFICATION_ID (Java): the id the app posts it under. */
export const NATIVE_NOTIFICATION_ID = 7301;

export function incomingCallTag(sessionId: number): string {
  return `incoming_call:${sessionId}`;
}

/** A listener ring's notification tag (server PushNotificationService::buildMessage; public/sw.js). */
export function ringTag(ringId: number): string {
  return `ring:${ringId}`;
}

export async function cancelIncomingCallNotification(sessionId: number): Promise<void> {
  await cancelByTag(incomingCallTag(sessionId), [String(NATIVE_NOTIFICATION_ID), '0']);
}

/**
 * #1009 item 1, class D: the listener's ring notification goes once the same
 * ring is on screen in the app (RingSheet). FCM draws it under the ring's
 * tag with id 0; the service worker under the same tag.
 */
export async function cancelRingNotification(ringId: number): Promise<void> {
  await cancelByTag(ringTag(ringId), ['0']);
}

async function cancelByTag(tag: string, nativeIds: string[]): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    if (Capacitor.getPlatform() !== 'android') return;
    try {
      await PushNotifications.removeDeliveredNotifications({
        notifications: nativeIds.map((id) => ({ id, tag, data: {} })),
      });
    } catch {
      /* nothing delivered, or the plugin is unavailable: nothing to cancel */
    }
    return;
  }
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const shown = await registration?.getNotifications({ tag });
    for (const n of shown ?? []) n.close();
  } catch {
    /* no service worker here */
  }
}

/**
 * Start cancelling on the three events above; returns the stop function.
 * Mounted once, app-wide (ListenerRingHost).
 */
export function watchIncomingCallDismissals(): () => void {
  const stopHandled = useIncomingCallStore.subscribe((state, prev) => {
    if (state.handled === prev.handled) return;
    for (const key of Object.keys(state.handled)) {
      const id = Number(key);
      if (!prev.handled[id]) void cancelIncomingCallNotification(id);
    }
  });
  const stopMissed = useActiveCohortSessionsStore.subscribe((state, prev) => {
    const missed = state.missedCall;
    if (missed && missed.session_id !== prev.missedCall?.session_id) {
      void cancelIncomingCallNotification(missed.session_id);
    }
  });
  return () => {
    stopHandled();
    stopMissed();
  };
}
